import type { PiniaPluginContext, StateTree } from 'pinia'
import type { ApiInstance } from '~/composables/base/createUseApi.ts'
import type { Path } from 'deep-pick-omit'
import { pick } from 'es-toolkit'
import { defu } from 'defu'
import type { PersistenceOptions } from 'pinia-plugin-persistedstate'
import type { MaybeArray } from '~/types/utils.ts'

declare module 'pinia' {
  export interface PiniaCustomProperties {
    currentLocale: Locale
    currentLocaleIsDutch: boolean

    api: ApiInstance

    onLogout: () => void
  }

  export interface DefineStoreOptionsBase<S, Store> {
    logout?:
      | boolean
      | {
          pick: Path<S>[]
        }
  }
}

function GlobalPropertiesPiniaPlugin({ store }: PiniaPluginContext) {
  const localeStore = useLocaleStore()

  store.api = markRaw(useApi())

  // A bit of a strange one, but Pinia automatically pretends computed properties are normal properties.
  // This means that `this.currentLocale` in a store actually returns the locale, while
  // this.currentLocale.value would return `undefined`.
  // However, if we define the property as a `ComputedRef` in the module above, TypeScript would
  // give an error, saying that the `this.currentLocale` is a `ComputedRef` and would need a `.value` to be used
  // in records and such.
  //
  // To fix this conflict, we can cast the computed property to `any` to bypass TypeScript's type checking.
  // This means it appears to be typed correctly, and it will work correctly, aside from these `any` casts.
  store.currentLocale = computed(() => localeStore.locale) as any
  store.currentLocaleIsDutch = computed(() => localeStore.localeIsDutch) as any
}

function PiniaLogout(context: PiniaPluginContext) {
  const { store, options } = context
  const { state, logout } = options

  return {
    onLogout: markRaw(() => {
      let newState: StateTree
      if (logout) {
        if (typeof logout === 'boolean') {
          return
        } else {
          const keptProperties = pick(store.$state, logout.pick)
          const tempState = state ? state() : {}
          newState = defu(keptProperties, tempState)
        }
      } else {
        newState = state ? state() : {}
      }

      store.$patch(($state) => {
        Object.assign($state, newState)
      })
    })
  }
}

export default defineNuxtPlugin(({ $pinia }) => {
  $pinia.use(GlobalPropertiesPiniaPlugin)
  $pinia.use(PiniaLogout)
})

// Pinia Persist Plugin helper

type RestoreFunction = (context: PiniaPluginContext) => void

function addRestoreFunction<S extends StateTree>(
  option: PersistenceOptions<S>
): PersistenceOptions<S> {
  if (option.afterHydrate) {
    const oldHydrate = option.afterHydrate

    const newRestoreFunction: RestoreFunction = (context) => {
      parseDateResponse(context.store)
      oldHydrate(context)
    }

    return {
      ...option,
      afterHydrate: newRestoreFunction
    }
  } else {
    return {
      ...option,
      afterHydrate: ({ store }) => {
        parseDateResponse(store)
      }
    }
  }
}

export function usePersist<S extends StateTree>(
  options: boolean | MaybeArray<PersistenceOptions<S>> | undefined
): MaybeArray<PersistenceOptions<S>> | undefined {
  if (typeof options === 'boolean') {
    return {
      afterHydrate: ({ store }) => {
        parseDateResponse(store)
      }
    }
  } else if (Array.isArray(options)) {
    return options.map((o) => addRestoreFunction(o))
  } else if (options) {
    return addRestoreFunction(options)
  }

  return undefined
}
